import type { MouseEvent } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import Icon from 'src/common/components/icons/Icon';
import { frostedPanelBg } from 'src/common/styles/frostedPanelStyles';
import {
  FlatMenuRow,
  MENU_INDENT_PX,
  applyHorizontalDragOffset,
  canIndentRow,
  canOutdentRow,
  flattenPagesToMenuRows,
  flatRowsToMenuUpdates,
  moveMenuSubtree,
  indentRow,
  outdentRow,
  projectDepthFromDragOffset,
} from 'src/helpers/publicPageMenuTree';
import { LanguageKeys } from 'src/common/constants/languages';
import {
  PUBLIC_PAGE_MENU_LOCATIONS,
  normalizeMenuLocations,
} from 'src/helpers/publicPageMenuLocations';
import { IPublicPage } from 'src/interfaces/PublicPage';
import usePublicPagesStore from 'src/stores/publicPagesStore';

import PagePathCaption from './PagePathCaption';

interface PagesMenuManagerProps {
  pages: IPublicPage[];
  onDelete: (id: string, e?: MouseEvent) => void;
}

interface MenuRowContentProps {
  row: FlatMenuRow;
  publicPagesById: Record<string, IPublicPage>;
  onDelete: PagesMenuManagerProps['onDelete'];
  onIndent: () => void;
  onOutdent: () => void;
  canIndent: boolean;
  canOutdent: boolean;
  dragHandleProps?: Record<string, unknown>;
  isDragOverlay?: boolean;
  isDragging?: boolean;
}

function MenuRowContent({
  row,
  publicPagesById,
  onDelete,
  onIndent,
  onOutdent,
  canIndent,
  canOutdent,
  dragHandleProps,
  isDragOverlay,
  isDragging,
}: MenuRowContentProps) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage ?? 'en') as keyof IPublicPage['menu']['labels'];
  const pathLang = lang as LanguageKeys;

  const label = row.page.menu.labels[lang] || row.page.key;
  const kindLabel =
    row.page.kind === 'group' ? t('system.pages.group') : t('system.pages.cmsPage');
  const isPublished = row.page.published;
  const publishedLabel = isPublished ? t('system.pages.published') : t('system.pages.unpublished');
  const menuLocations = normalizeMenuLocations(row.page.menu);
  const menuLocationLabel = (loc: (typeof PUBLIC_PAGE_MENU_LOCATIONS)[number]) =>
    loc === 'top' ? t('system.pages.menuLocationTop') : t('system.pages.menuLocationFooter');

  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.75,
        px: 1,
        mb: 0.75,
        ml: `${row.depth * MENU_INDENT_PX}px`,
        bgcolor: isDragOverlay ? 'background.paper' : theme => frostedPanelBg(theme, isPublished ? 0.85 : 0.55),
        boxShadow: isDragOverlay ? 4 : 0,
        borderColor: isDragging ? 'primary.main' : isPublished ? 'divider' : 'action.disabled',
        borderStyle: isPublished ? 'solid' : 'dashed',
        opacity: isPublished ? 1 : 0.88,
      }}
    >
      <Box
        {...dragHandleProps}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: dragHandleProps ? (isDragOverlay ? 'grabbing' : 'grab') : 'default',
          color: 'text.secondary',
          touchAction: 'none',
        }}
        aria-label={t('system.pages.dragHandle')}
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>

      {row.page.menu.icon ? (
        <Icon icon={row.page.menu.icon} sx={{ fontSize: '3rem', color: 'text.secondary' }} />
      ) : null}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: isPublished ? 'text.primary' : 'text.secondary' }}
          noWrap
        >
          {label}
        </Typography>
        <PagePathCaption page={row.page} lang={pathLang} publicPagesById={publicPagesById} />
      </Box>

      <Chip
        size="small"
        label={publishedLabel}
        color={isPublished ? 'success' : 'default'}
        variant={isPublished ? 'outlined' : 'filled'}
        sx={{ display: { xs: 'none', sm: 'flex' } }}
      />

      {menuLocations.length > 0 ? (
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 0.5, flexShrink: 0 }}>
          {PUBLIC_PAGE_MENU_LOCATIONS.filter(loc => menuLocations.includes(loc)).map(loc => (
            <Chip key={loc} size="small" label={menuLocationLabel(loc)} variant="outlined" />
          ))}
        </Box>
      ) : null}

      <Chip size="small" label={kindLabel} variant="outlined" sx={{ display: { xs: 'none', md: 'flex' } }} />

      {row.depth > 0 ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontStyle: 'italic', display: { xs: 'none', md: 'block' } }}
        >
          {t('system.pages.subItem')}
        </Typography>
      ) : null}

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip title={t('system.pages.outdent')}>
          <span>
            <IconButton size="small" disabled={!canOutdent} onClick={onOutdent}>
              <KeyboardArrowLeftIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('system.pages.indent')}>
          <span>
            <IconButton size="small" disabled={!canIndent} onClick={onIndent}>
              <KeyboardArrowRightIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('participants.edit')}>
          <IconButton size="small" component={Link} to={`/dashboard/system/pages/${row.page.id}`}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('participants.delete')}>
          <IconButton
            size="small"
            color="error"
            onClick={e => onDelete(row.page.id, e)}
          >
            <DeleteForeverIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  );
}

interface SortableRowProps {
  row: FlatMenuRow;
  publicPagesById: Record<string, IPublicPage>;
  onDelete: PagesMenuManagerProps['onDelete'];
  onIndent: () => void;
  onOutdent: () => void;
  canIndent: boolean;
  canOutdent: boolean;
}

function SortableMenuRow(props: SortableRowProps) {
  const { row } = props;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
  };

  return (
    <Box ref={setNodeRef} style={style}>
      <MenuRowContent
        {...props}
        dragHandleProps={{ ...attributes, ...listeners }}
        isDragging={isDragging}
      />
    </Box>
  );
}

export default function PagesMenuManager({ pages, onDelete }: PagesMenuManagerProps) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const reorderMenu = usePublicPagesStore(s => s.reorderMenu);

  const publicPagesById = useMemo(
    () => Object.fromEntries(pages.filter(p => p.id).map(p => [p.id!, p])),
    [pages],
  );

  const initialFlat = useMemo(() => flattenPagesToMenuRows(pages), [pages]);
  const [flatRows, setFlatRows] = useState<FlatMenuRow[]>(initialFlat);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [previewDepth, setPreviewDepth] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const dragOffsetXRef = useRef(0);
  const pointerStartXRef = useRef(0);

  useEffect(() => {
    setFlatRows(flattenPagesToMenuRows(pages));
  }, [pages]);

  useEffect(() => {
    if (!activeId) return undefined;

    const onPointerMove = (ev: PointerEvent) => {
      const offsetX = ev.clientX - pointerStartXRef.current;
      dragOffsetXRef.current = offsetX;
      const index = flatRows.findIndex(r => r.id === activeId);
      if (index >= 0) {
        setPreviewDepth(projectDepthFromDragOffset(flatRows, index, offsetX));
      }
    };

    window.addEventListener('pointermove', onPointerMove);
    return () => window.removeEventListener('pointermove', onPointerMove);
  }, [activeId, flatRows]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const activeRow = activeId ? flatRows.find(r => r.id === activeId) : undefined;

  const persistOrder = useCallback(
    async (nextFlat: FlatMenuRow[]) => {
      const previous = flatRows;
      setFlatRows(nextFlat);
      setIsSaving(true);
      try {
        const updates = flatRowsToMenuUpdates(nextFlat);
        await reorderMenu(updates);
        enqueueSnackbar(t('system.pages.menuOrderSaved'), { variant: 'success' });
      } catch {
        setFlatRows(previous);
        enqueueSnackbar(t('system.pages.menuOrderError'), { variant: 'error' });
      } finally {
        setIsSaving(false);
      }
    },
    [flatRows, reorderMenu, enqueueSnackbar, t],
  );

  const handleDragStart = (event: DragStartEvent) => {
    const activator = event.activatorEvent;
    pointerStartXRef.current =
      activator && 'clientX' in activator ? (activator.clientX as number) : 0;
    dragOffsetXRef.current = 0;
    setPreviewDepth(null);
    setActiveId(String(event.active.id));
  };

  const handleDragMove = (event: DragMoveEvent) => {
    if (Math.abs(event.delta.x) > Math.abs(dragOffsetXRef.current)) {
      dragOffsetXRef.current = event.delta.x;
    }
    const index = flatRows.findIndex(r => r.id === event.active.id);
    if (index >= 0) {
      setPreviewDepth(projectDepthFromDragOffset(flatRows, index, dragOffsetXRef.current));
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const offsetX = dragOffsetXRef.current;
    dragOffsetXRef.current = 0;
    setActiveId(null);
    setPreviewDepth(null);

    const activeIndex = flatRows.findIndex(r => r.id === active.id);
    if (activeIndex < 0) return;

    if (!over) {
      const nestedOnly = applyHorizontalDragOffset(flatRows, activeIndex, offsetX);
      if (nestedOnly !== flatRows) void persistOrder(nestedOnly);
      return;
    }

    let nextRows: FlatMenuRow[] | null;

    if (active.id === over.id) {
      nextRows = applyHorizontalDragOffset(flatRows, activeIndex, offsetX);
    } else {
      nextRows = moveMenuSubtree(flatRows, String(active.id), String(over.id), offsetX);
    }

    if (!nextRows || nextRows === flatRows) return;

    void persistOrder(nextRows);
  };

  const handleDragCancel = () => {
    dragOffsetXRef.current = 0;
    setActiveId(null);
    setPreviewDepth(null);
  };

  const handleIndent = (index: number) => {
    void persistOrder(indentRow(flatRows, index));
  };

  const handleOutdent = (index: number) => {
    void persistOrder(outdentRow(flatRows, index));
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('system.pages.menuManagerHint')}
      </Typography>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext items={flatRows.map(r => r.id)} strategy={verticalListSortingStrategy}>
          <Box sx={{ opacity: isSaving ? 0.6 : 1, pointerEvents: isSaving ? 'none' : 'auto' }}>
            {flatRows.map((row, index) => (
              <SortableMenuRow
                key={row.id}
                row={row}
                publicPagesById={publicPagesById}
                onDelete={onDelete}
                onIndent={() => handleIndent(index)}
                onOutdent={() => handleOutdent(index)}
                canIndent={canIndentRow(flatRows, index)}
                canOutdent={canOutdentRow(flatRows, index)}
              />
            ))}
          </Box>
        </SortableContext>

        <DragOverlay>
          {activeRow ? (
            <MenuRowContent
              row={
                previewDepth != null ? { ...activeRow, depth: previewDepth } : activeRow
              }
              publicPagesById={publicPagesById}
              onDelete={onDelete}
              onIndent={() => undefined}
              onOutdent={() => undefined}
              canIndent={false}
              canOutdent={false}
              isDragOverlay
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    </Box>
  );
}
